import { isDisabled } from './init'
import { getLanguage } from './signals/language'
import { getTimezone } from './signals/timezone'
import { getViewportDimensions } from './signals/viewport'
import { isInitialized } from './state'
import type { FingerprintResult, SignalsRecord } from './types'
import { VERSION } from './version'

/**
 * Read the current fingerprint signals.
 *
 * Synchronous in Phase 1 — all 3 browser-only signals are sync getters
 * (`viewport_dimensions`, `timezone`, `language`). When Tier 1/2 enrichers
 * (canvas, WebGL, audio, fonts) ship in Phase 2, this function will be
 * promoted to async; consumers should expect a future signature change.
 *
 * The 12 remaining signals (`device_id`, `user_agent`, and the 10 UA-derived
 * fields) are composed server-side by Bifrost.
 *
 * Calling `get` before `init` is a programming error and throws.
 */
export function get(): FingerprintResult {
  if (!isInitialized()) {
    throw new Error(
      '[fingerprint-collector] `get()` called before `init()`. ' +
        'Call `init()` once at app bootstrap.'
    )
  }

  const collectedAt = Date.now()

  if (isDisabled()) {
    return { signals: emptySignals(), version: VERSION, collectedAt }
  }

  const signals = composeSignals()
  return { signals, version: VERSION, collectedAt }
}

function composeSignals(): SignalsRecord {
  return {
    viewport_dimensions: getViewportDimensions(),
    timezone: getTimezone(),
    language: getLanguage(),
  }
}

/**
 * Default values used when the collector is disabled (debug opt-out).
 */
function emptySignals(): SignalsRecord {
  return {
    viewport_dimensions: '',
    timezone: '',
    language: '',
  }
}
